import { Avatar, Box, Grow, InputAdornment, makeStyles, Paper, Typography } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import clsx from "clsx";
import { ethers } from "ethers";
import { useEffect, useMemo, useRef, useState } from "react";
import Empty from "src/components/utils/Empty";
import { NETWORK, THEME_MODE } from "src/config/constants";
import { getNetworkTokens } from "src/config/tokens";
import { DEFAULT_VALUE, useAddAssetContext } from "src/context/monitoring/addAssetContext";
import StyledTextField from "src/view/PoolMonitoring/Overview/AssetConfigOption/components/StyledTextField.js";
import { CHAIN_ALIASES } from "src/wallet-connection/chains";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";

const useStyle = makeStyles((theme) => ({
  box: {
    position: "absolute",
    zIndex: 1000,
    maxHeight: "300px",
    overflow: "scroll",
    width: "100%",
  },
  paper: {
    borderRadius: 0,
    padding: 0,
    paddingBottom: 0,
  },
  growBox: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "1rem",
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.type === THEME_MODE.DARK ? "#031527" : "#F2F4F7",
    },
  },
  growText: {
    color: theme.palette.text.hint,
  },
  iconClose: {
    color: theme.palette.text.hint,
    cursor: "pointer",
  },
  cleanIcon: {
    cursor: "pointer",
  },
  hideCleanIcon: {
    visibility: "hidden",
  },
}));

export default function TokenSelect({
  setValue,
  data,
  setHelperTextAddress,
  helperTextAddress,
  setSelectedOracle,
  currentTokenList,
  ...props
}) {
  const cls = useStyle();
  const { chain } = useWeb3Connect();
  const [isOpen, setIsOpen] = useState(false);
  const mounted = useRef(false);
  const [tokenAddress, setTokenAddress] = useState("");
  const { stepStatus } = useAddAssetContext();
  const disableAll = !(stepStatus.activeStep == 0);

  const tokens = useMemo(() => {
    let rawTokens = {};
    if (chain.chainId === CHAIN_ALIASES.BSC_MAINNET) rawTokens = getNetworkTokens(NETWORK.BSC_MAINNET);
    else if (chain.chainId === CHAIN_ALIASES.BSC_TESTNET) rawTokens = getNetworkTokens(NETWORK.BSC_TESTNET);
    for (const tokenAddress of currentTokenList) {
      rawTokens[tokenAddress.toLowerCase()] = undefined;
    }
    return rawTokens;
  }, [chain.chainId, currentTokenList]);

  const tokenList = useMemo(() => {
    if (tokens) {
      const tokenObject = Object.entries(tokens);
      return tokenObject
        .filter((token, _) => {
          if (token.length > 0) {
            if (!token[1]) return false;
            const lowerTokenAddress = tokenAddress.toLowerCase();
            const addressCheck = token[0].includes(lowerTokenAddress);
            const symbolCheck = token[1]["symbol"].toLowerCase().includes(lowerTokenAddress);
            return addressCheck || symbolCheck;
          } else return true;
        })
        .map((token, _) => {
          return { address: token[0], ...token[1] };
        });
    } else return [];
  }, [tokens, tokenAddress]);

  function validateValue() {
    const lowerValue = tokenAddress.toLowerCase();
    if (tokenAddress.replaceAll(" ", "") === "") setHelperTextAddress("This field is required");
    else if (!ethers.utils.isAddress(tokenAddress)) setHelperTextAddress("Token address is invalid");
    else if (!tokens[lowerValue]) setHelperTextAddress("Token is not support");
    else setHelperTextAddress(null);
  }

  useEffect(() => {
    if (mounted.current) validateValue();
    mounted.current = true;
  }, [tokenAddress]);

  useEffect(() => {
    if (helperTextAddress == null || helperTextAddress == "Address has been duplicated") {
      const selectedAddresses = Object.values(data).filter((item) => tokenAddress == item.data.address);
      if (selectedAddresses.length >= 2) setHelperTextAddress("Address has been duplicated");
      else setHelperTextAddress(null);
    }
  }, [tokenAddress, data]);

  function onGrowBoxClick(token) {
    setTokenAddress(token["address"]);
    setIsOpen(false);
  }

  useEffect(() => {
    if (tokenAddress?.length > 0) {
      const _token = tokens[tokenAddress.toLowerCase()];
      if (_token !== undefined) {
        setValue((draft) => {
          draft["address"] = tokenAddress.toLowerCase();
          draft["symbol"] = _token["symbol"];
          draft["decimal"] = _token["decimal"];
          draft["image"] = _token["image"];
          draft["loanToValue"] = _token["loanToValue"];
          draft["lt"] = _token["lt"];
          draft["reserveFactor"] = _token["reserveFactor"];
          draft["liquidationBonus"] = _token["liquidationBonus"];
          draft["utilizationOptimal"] = _token["utilizationOptimal"];
          draft["slope1"] = _token["slope1"];
          draft["slope2"] = _token["slope2"];
          draft["baseInterestRate"] = _token["baseInterestRate"];
        });
        if (_token["oracle"]) setSelectedOracle(_token["oracle"]);
        else setSelectedOracle({ address: undefined, oracle: undefined });
      } else {
        setValue(DEFAULT_VALUE);
        setSelectedOracle({ address: undefined, oracle: undefined });
      }
    } else {
      setValue(DEFAULT_VALUE);
      setSelectedOracle({ address: undefined, oracle: undefined });
    }
  }, [tokenAddress]);

  const handleClickOutside = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  function handleDeleteAddress(e) {
    e.stopPropagation();
    setTokenAddress("");
  }

  return (
    <Box position="relative" {...props}>
      <StyledTextField
        textProps={{
          disabled: disableAll,
          placeholder: "Search name or paste address",
          fullWidth: true,
          onClick: (event) => {
            event.stopPropagation();
            setIsOpen(true);
          },
          InputProps: {
            endAdornment: (
              <InputAdornment position="end" onClick={handleDeleteAddress}>
                <CloseOutlined
                  fontSize="small"
                  className={clsx({
                    [cls.cleanIcon]: tokenAddress.length > 0,
                    [cls.hideCleanIcon]: tokenAddress.length === 0,
                  })}
                />
              </InputAdornment>
            ),
          },
          value: tokenAddress,
          onChange: (event) => setTokenAddress(event.target.value),
          error: helperTextAddress !== null,
          helperText: helperTextAddress,
        }}
      />
      {isOpen && (
        <Box className={cls.box}>
          <Grow in={true}>
            <Paper className={cls.paper}>
              {(() => {
                if (tokenList.length > 0)
                  return (
                    <>
                      {tokenList.map((token, index) => {
                        return (
                          <Box key={index} className={cls.growBox} onClick={() => onGrowBoxClick(token)}>
                            <Avatar style={{ marginRight: 10, width: "25px", height: "25px" }} src={token["image"]} />
                            <Typography variant="subtitle2" className={cls.growText}>
                              {token["symbol"]}
                            </Typography>
                          </Box>
                        );
                      })}
                    </>
                  );
                else
                  return (
                    <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: "100%" }}>
                      <Empty title="No Data" iconProps={{ style: { fontSize: "60px" } }} />
                    </Box>
                  );
              })()}
            </Paper>
          </Grow>
        </Box>
      )}
    </Box>
  );
}
